import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/services/api.ts";
import { useAuth } from "@/auth/AuthContext";
import type { DashboardFilters, EventFilters } from "@/types";

export function useAnalytics(filters?: DashboardFilters) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["analytics", effectiveCompanyId, filters],
    queryFn: () => api.dashboard.getAnalytics(filters),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!effectiveCompanyId,
  });
}

export function useEvents(
  filters?: EventFilters,
  limit = 50,
  offset = 0,
) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["events", effectiveCompanyId, filters, limit, offset],
    queryFn: () => api.dashboard.getEvents(filters, limit, offset),
    staleTime: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!effectiveCompanyId,
  });
}

export function useFilterMetadata() {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["filterMetadata", effectiveCompanyId],
    queryFn: () => api.dashboard.getFilterMetadata(),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: !!effectiveCompanyId,
  });
}

export function useSecurityOverview(days: number = 7) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["securityOverview", effectiveCompanyId, days],
    queryFn: () => api.securityOverview.getData(days),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!effectiveCompanyId,
  });
}

export function useCredentialHealth() {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["credentialHealth", effectiveCompanyId],
    queryFn: () => api.credentialHealth.getData(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!effectiveCompanyId,
  });
}

export function useSnapshotInfo() {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["snapshotInfo", effectiveCompanyId],
    queryFn: () => api.snapshot.getInfo(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!effectiveCompanyId,
  });
}

export function useNeverAuthenticatedIps(enabled: boolean) {
  const { effectiveCompanyId } = useAuth();
  return useInfiniteQuery({
    queryKey: ["neverAuthIps", effectiveCompanyId],
    queryFn: ({ pageParam }) => api.securityOverview.getQueriedIps(true, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.continuation ?? undefined,
    enabled: enabled && !!effectiveCompanyId,
    staleTime: 60 * 1000,
  });
}

